import { Client, ReportPage, Tool } from '../../../data/models/clients.model';
import { User, UserRole } from '../../../data/models/user.model';
import { fmtRoute, isFmtPage } from '../../../portal/financial-modeling/fmt-route.util';
import { AppRoute, FmtPage } from '../../constants/route';
import { base64EncodeUrlSafe, decodeUrlSafeBase64 } from '../../utils/base64.util';
import { getClientLandingNavigation } from '../../utils/clients.util';
import { LocalStorageUtil } from '../../utils/local-storage.util';

export enum NavTool {
	Manage = 'MANAGE',
	Reports = 'REPORTS',
	Finance = 'FINANCE',
	Maps = 'MAPS',
}

export interface NavPage {
	label: string;
	route: string[];
	icon?: string;
}

export interface NavSection {
	label: string;
	pages: NavPage[];
}

export interface NavContext {
	isInternal: boolean;
	isAdmin: boolean;
	client: Client | null;
}

export interface NavToolConfig {
	tool: NavTool;
	label: string;
	icon: string;
	expandedTitle: string;
	visible: (ctx: NavContext) => boolean;
	pages?: (ctx: NavContext) => NavPage[];
	sections?: (ctx: NavContext) => NavSection[];
}

export const NAV_TOOL_CONFIGS: NavToolConfig[] = [
	{
		tool: NavTool.Manage,
		label: 'Admin',
		expandedTitle: 'Administration',
		icon: 'admin_panel_settings',
		visible: (ctx) => ctx.isInternal || ctx.isAdmin,
		pages: (ctx) => {
			const pages: NavPage[] = [];
			if (ctx.isInternal) {
				pages.push({ label: 'Clients', route: [AppRoute.Clients], icon: 'checklist' });
				if (ctx.isAdmin) {
					pages.push({
						label: 'DIXON Users',
						route: [AppRoute.InternalUsers],
						icon: 'group',
					});
					pages.push({
						label: 'Client Users',
						route: [AppRoute.Users],
						icon: 'group',
					});
				}
			} else if (ctx.isAdmin) {
				pages.push({ label: 'Users', route: [AppRoute.Users], icon: 'group' });
			}
			return pages;
		},
	},
	{
		tool: NavTool.Reports,
		label: 'Data',
		expandedTitle: 'Rapid LPR Tool',
		icon: 'directions_car',
		visible: () => true,
		pages: () => [],
	},
	{
		tool: NavTool.Finance,
		label: 'Modeling',
		expandedTitle: 'Financial Modeling Tool',
		icon: 'paid',
		visible: (ctx) => !!ctx.client?.tools?.includes(Tool.FINANCIAL_MODELING),
		sections: () => [
			{
				label: 'Configuration',
				pages: [
					{ label: 'Overview', route: [AppRoute.FinancialModeling, FmtPage.Overview], icon: 'dashboard' },
					{ label: 'Zones', route: [AppRoute.FinancialModeling, FmtPage.Zones], icon: 'place' },
					{
						label: 'Schedule & Holidays',
						route: [AppRoute.FinancialModeling, FmtPage.Schedule],
						icon: 'calendar_month',
					},
					{ label: 'Rate Structures', route: [AppRoute.FinancialModeling, FmtPage.Rates], icon: 'paid' },
					{ label: 'Special Events', route: [AppRoute.FinancialModeling, FmtPage.Events], icon: 'event' },
					{ label: 'Permits', route: [AppRoute.FinancialModeling, FmtPage.Permits], icon: 'badge' },
					{ label: 'Staffing', route: [AppRoute.FinancialModeling, FmtPage.Staffing], icon: 'groups' },
					{ label: 'Equipment & Tech', route: [AppRoute.FinancialModeling, FmtPage.Equipment], icon: 'construction' },
					{ label: 'Other Rev/Exp', route: [AppRoute.FinancialModeling, FmtPage.Other], icon: 'receipt_long' },
				],
			},
			{
				label: 'Analysis',
				pages: [
					{ label: 'Projections', route: [AppRoute.FinancialModeling, FmtPage.Results], icon: 'trending_up' },
					{ label: 'Compare Scenarios', route: [AppRoute.FinancialModeling, FmtPage.Compare], icon: 'compare_arrows' },
					{ label: 'Reports & Export', route: [AppRoute.FinancialModeling, FmtPage.Reports], icon: 'lab_profile' },
					{ label: 'Quick Edit Grid', route: [AppRoute.FinancialModeling, FmtPage.Grid], icon: 'table_edit' },
				],
			},
		],
	},
	{
		tool: NavTool.Maps,
		label: 'Maps',
		expandedTitle: 'Maps',
		icon: 'public',
		visible: (ctx) => !!ctx.client?.tools?.includes(Tool.ADVANCED_MAPS),
		pages: () => [
			{ label: 'Inventory Map', route: [AppRoute.AdvancedMaps], icon: 'map' },
			{ label: 'Settings', route: [AppRoute.AdvancedMaps, AppRoute.MapSettings], icon: 'settings' },
		],
	},
];

export function buildNavContext(client: Client | null): NavContext {
	return {
		isInternal: LocalStorageUtil.getIsInternal(),
		isAdmin: LocalStorageUtil.getRole() === UserRole.ADMIN,
		client,
	};
}

export function getVisibleTools(ctx: NavContext): NavToolConfig[] {
	return NAV_TOOL_CONFIGS.filter((config) => config.visible(ctx));
}

const LPR_REPORT_PAGE_ICONS: Record<string, string> = {
	'Welcome!': 'home',
	Inventory: 'local_parking',
	Occupancy: 'bar_chart',
	'High Occupancy': 'trending_up',
	Turnover: 'schedule',
	'Length of Stay': 'schedule',
	'Collection Coverage': 'route',
	'Average Daily Collection': 'monitoring',
	'Average Daily Coverage': 'monitoring',
	Payments: 'attach_money',
	'Transaction Revenue': 'attach_money',
	Permits: 'verified',
	'Daily Parkers': 'calendar_today',
	'Weekly Parkers': 'calendar_view_week',
	'Repeat Parkers': 'sync',
	'Parking Compliance': 'check_circle',
	Citations: 'receipt_long',
	Coverage: 'route',
	Compliance: 'verified',
};

export function getSectionsForTool(tool: NavTool, ctx: NavContext, reportPages: ReportPage[] = []): NavSection[] {
	const config = NAV_TOOL_CONFIGS.find((c) => c.tool === tool);
	if (config?.sections) {
		return config.sections(ctx);
	}
	const pages = tool === NavTool.Reports ? getReportNavPages(reportPages) : (config?.pages?.(ctx) ?? []);
	if (pages.length === 0) {
		return [];
	}
	return [{ label: '', pages }];
}

export function getPagesForTool(tool: NavTool, ctx: NavContext, reportPages: ReportPage[] = []): NavPage[] {
	return getSectionsForTool(tool, ctx, reportPages).flatMap((section) => section.pages);
}

export function getReportNavPages(reportPages: ReportPage[]): NavPage[] {
	if (reportPages.length) {
		return reportPages.map((page) => ({
			label: page.displayName,
			route: [AppRoute.LprReport, base64EncodeUrlSafe(page.name)],
			icon: LPR_REPORT_PAGE_ICONS[page.displayName],
		}));
	}
	return [{ label: 'Dashboard', route: [AppRoute.LprReport, ''], icon: 'monitoring' }];
}

export function getClientChangeNavigation(client: Client | null, currentUrl: string): string[] {
	const currentTool = routeToNavTool(currentUrl);
	if (currentTool == null) {
		return getClientLandingNavigation(client);
	}

	const ctx = buildNavContext(client);
	const isToolVisible = getVisibleTools(ctx).some((config) => config.tool === currentTool);
	if (!isToolVisible) {
		return getClientLandingNavigation(client);
	}

	return getNavigationForTool(currentTool, client, currentUrl);
}

export function routeToNavTool(url: string): NavTool | null {
	const segments = url.split('?')[0].split('/').filter(Boolean);
	const first = segments[0];

	if (!isAppRoute(first)) return null;

	switch (first) {
		case AppRoute.Clients:
		case AppRoute.InternalUsers:
		case AppRoute.Users:
			return NavTool.Manage;
		case AppRoute.LprReport:
			return NavTool.Reports;
		case AppRoute.FinancialModeling:
			return NavTool.Finance;
		case AppRoute.AdvancedMaps:
			return NavTool.Maps;
		default:
			return null;
	}
}

export function isAppRoute(route: string): route is AppRoute {
	return Object.values(AppRoute).includes(route as AppRoute);
}

export function getDefaultPageForTool(tool: NavTool, client: Client | null, user: User | null): string[] {
	switch (tool) {
		case NavTool.Manage:
			if (user?.isInternal) {
				return [AppRoute.Clients];
			}
			return [AppRoute.Users];
		case NavTool.Reports: {
			const page: string[] = [AppRoute.LprReport];
			const firstPage = client?.reportPages?.[0];
			if (firstPage) {
				page.push(base64EncodeUrlSafe(firstPage.name));
			}
			return page;
		}
		case NavTool.Finance:
			return [AppRoute.FinancialModeling];
		case NavTool.Maps:
			return [AppRoute.AdvancedMaps];
	}
}

function getNavigationForTool(tool: NavTool, client: Client | null, currentUrl: string): string[] {
	const segments = currentUrl.split('?')[0].split('/').filter(Boolean) as AppRoute[];

	switch (tool) {
		case NavTool.Manage: {
			const first = segments[0];
			if (first === AppRoute.InternalUsers || first === AppRoute.Users) {
				return [first];
			}
			return [AppRoute.Clients];
		}
		case NavTool.Reports: {
			const encodedPage = segments[1];
			if (encodedPage) {
				const pageName = decodeUrlSafeBase64(encodedPage);
				const matchingPage = client?.reportPages?.find((page) => page.name === pageName);
				if (matchingPage) {
					return [AppRoute.LprReport, base64EncodeUrlSafe(matchingPage.name)];
				}
			}
			const firstPage = client?.reportPages?.[0];
			return firstPage ? [AppRoute.LprReport, base64EncodeUrlSafe(firstPage.name)] : [AppRoute.LprReport];
		}
		case NavTool.Finance: {
			const scenarioId = segments[1];
			const page = segments[2];
			if (scenarioId != null && isFmtPage(page)) {
				return fmtRoute(scenarioId, page);
			}
			return [AppRoute.FinancialModeling];
		}
		case NavTool.Maps:
			return segments[1] === AppRoute.MapSettings
				? [AppRoute.AdvancedMaps, AppRoute.MapSettings]
				: [AppRoute.AdvancedMaps];
	}
}
